import React, {
  PropsWithChildren,
  useContext,
  ReactElement,
  MouseEventHandler,
} from 'react';

import PropTypes from 'prop-types';
import { ColumnInstance } from 'react-table';
import { CSSObject } from 'styled-components';

import { stylePropType, StylableComponent } from '@adsk/alloy-react-helpers';
import {
  ArrowDownIcon,
  ArrowUpDownIcon,
  ArrowUpIcon,
} from '@adsk/alloy-react-icon';
import theme from '@adsk/alloy-react-theme';

import TablePropTypes from './propTypes';
import TableSizeThemeContext from './TableSizeThemeContext';

export type TableHeaderCellProps<D extends Record<string, unknown>> =
  StylableComponent<HTMLDivElement> & {
    column: ColumnInstance<D>;
    isPrimaryRow?: boolean;
    isResizingAnyColumn?: boolean;
  };

type TableResizerProps = {
  style?: CSSObject;
};

type SortByToggleProps = { title?: string; onClick?: MouseEventHandler };

const TableHeaderCell = <D extends Record<string, unknown>>({
  column,
  children,
  isPrimaryRow,
  isResizingAnyColumn,
  style,
  ...props
}: PropsWithChildren<TableHeaderCellProps<D>>): ReactElement => {
  const { tableSizeTheme } = useContext(TableSizeThemeContext);
  const {
    title: _ignoredTitle,
    onClick: onSortByClick,
    ...sortProps
  }: SortByToggleProps = column.canSort ? column.getSortByToggleProps() : {};
  const { style: resizerStyle, ...resizerProps }: TableResizerProps =
    column.canResize && column.getResizerProps ? column.getResizerProps() : {};
  const isPlaceholderHeaderCell = column.hasOwnProperty('placeholderOf');

  return (
    <div
      {...sortProps}
      onMouseUp={(e) => {
        if (column.isResizing || isResizingAnyColumn) return;
        onSortByClick?.(e);
      }}
      css={[
        {
          display: 'flex',
          alignItems: 'center',
          maxWidth: column.maxWidth,
          borderBottomWidth: 1,
          borderBottomStyle: 'solid',
          backgroundColor: theme.colors.white,
          '.tableHeaderCellSortIcon': {
            marginLeft: 'auto',
            minWidth: 20,
          },
          padding: `0 ${tableSizeTheme.cellPadding}px`,
          ...(column.canResize
            ? {
                '&:hover': {
                  '& > [role="separator"]': {
                    backgroundColor: theme.colors.charcoal200,
                  },
                },
              }
            : {}),
          ...(column.canSort
            ? {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: theme.colors.charcoal100,
                  '& > .tableHeaderCellSortIcon': {
                    display: 'inherit',
                  },
                  '& > [role="separator"]': {
                    backgroundColor: theme.colors.charcoal200,
                  },
                },
              }
            : {}),
          ...(isPrimaryRow
            ? {
                borderColor: theme.colors.charcoal200,
                fontWeight: theme.typography.bodySmallBold.fontWeight,
              }
            : {
                borderColor: theme.colors.charcoal100,
                borderRightWidth: 1,
                borderRightStyle: 'solid',
                borderRightColor: theme.colors.charcoal100,
                justifyContent: 'center',
                '&:last-child': {
                  borderRightWidth: 0,
                },
              }),
          ...(isPlaceholderHeaderCell ? { borderRightWidth: 0 } : {}),
        },
        style,
      ]}
      {...props}
    >
      {React.isValidElement(children) ? (
        React.cloneElement(children, { column, tableSizeTheme })
      ) : (
        <React.Fragment>
          {column.render('Header')}
          {column.canSort && !column.isSorted && (
            <ArrowUpDownIcon
              className="tableHeaderCellSortIcon"
              size={20}
              style={{
                display: 'none',
              }}
            />
          )}
          {column.canSort &&
            column.isSorted &&
            (column.isSortedDesc ? (
              <ArrowDownIcon size={20} className="tableHeaderCellSortIcon" />
            ) : (
              <ArrowUpIcon size={20} className="tableHeaderCellSortIcon" />
            ))}
        </React.Fragment>
      )}
      {column.canResize && !isPlaceholderHeaderCell && (
        <div
          {...resizerProps}
          onClick={(e) => {
            // Disable click event bubbling to prevent sorting column when users are
            // trying to resize
            e.stopPropagation();
            // Merging in onClick event handler to protect against changes to the
            // underlying react-table api
            return (resizerProps as { onClick?: MouseEventHandler }).onClick?.(
              e
            );
          }}
          css={[
            {
              zIndex: 1,
              width: 3,
              height: '100%',
              position: 'absolute',
              right: 0,
              top: 0,
              ...(column.isResizing
                ? { background: theme.colors.adskBlue300 }
                : {}),
            },
            resizerStyle,
          ]}
        />
      )}
    </div>
  );
};

TableHeaderCell.propTypes = {
  children: PropTypes.node,
  column: TablePropTypes.column.isRequired,
  style: stylePropType,
  isPrimaryRow: PropTypes.bool,
};

export default TableHeaderCell;
